import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  User,
  MapPin,
  LogOut,
  HelpCircle,
  Info,
  Navigation,
  Gift,
  Wallet,
  PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
  APP_VERSION,
} from "@/config/base";
import { useProfile } from "@/hooks/useProfile";
import { useTour } from "@/hooks/useTour";
import {
  ProfileHeader,
  ProfileMenuItem,
  ProfileSection,
  PersonalDataSheet,
  LocationPermissionSheet,
  ProfilePageSkeleton,
} from "@/components/profile";

export const Route = createFileRoute("/app/perfil")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({
        to: "/auth/identify",
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const tour = useTour();
  const {
    user,
    isLoading,
    isEditing,
    isSaving,
    formData,
    errors,
    handleEdit,
    handleCancel,
    handleSave,
    handleLogout,
    updateFormField,
    formatDate,
    formatCPF,
  } = useProfile();

  const [personalDataOpen, setPersonalDataOpen] = useState(false);
  const [locationPermissionOpen, setLocationPermissionOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  if (isLoading) {
    return <ProfilePageSkeleton />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Erro ao carregar perfil</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 max-w-2xl py-6 space-y-4">
        <ProfileHeader
          nome={user.nome}
          telefone={user.telefone}
        />

        <ProfileSection>
          <ProfileMenuItem onClick={() => setPersonalDataOpen(true)}>
            <ProfileMenuItem.Icon icon={User} />
            <ProfileMenuItem.Label>Dados Pessoais</ProfileMenuItem.Label>
            <ProfileMenuItem.Chevron />
          </ProfileMenuItem>
          <ProfileMenuItem onClick={() => navigate({ to: "/app/perfil/indicacoes" })} data-tour="profile-referral">
            <ProfileMenuItem.Icon icon={Gift} />
            <ProfileMenuItem.Label description="Compartilhe seu código e ganhe em dinheiro">
              Indique e Ganhe
            </ProfileMenuItem.Label>
            <ProfileMenuItem.Chevron />
          </ProfileMenuItem>
          <ProfileMenuItem onClick={() => navigate({ to: "/app/carteira" })}>
            <ProfileMenuItem.Icon icon={Wallet} />
            <ProfileMenuItem.Label description="Consulte seu saldo e extrato">
              Carteira
            </ProfileMenuItem.Label>
            <ProfileMenuItem.Chevron />
          </ProfileMenuItem>
          <ProfileMenuItem onClick={() => navigate({ to: "/app/perfil/enderecos" })} data-tour="profile-addresses">
            <ProfileMenuItem.Icon icon={MapPin} />
            <ProfileMenuItem.Label description="Destinatários e remetentes salvos">
              Endereços
            </ProfileMenuItem.Label>
            <ProfileMenuItem.Chevron />
          </ProfileMenuItem>
        </ProfileSection>

        <ProfileSection title="Configurações">
          <ProfileMenuItem onClick={() => setLocationPermissionOpen(true)}>
            <ProfileMenuItem.Icon icon={Navigation} />
            <ProfileMenuItem.Label description="Gerenciar permissão de GPS">
              Localização
            </ProfileMenuItem.Label>
            <ProfileMenuItem.Chevron />
          </ProfileMenuItem>
        </ProfileSection>

        <div>
          <div className="px-4 py-3">
            <h3 className="text-sm font-medium text-slate-500">Mais opções</h3>
          </div>
          <div className="divide-y divide-slate-100">
            <ProfileMenuItem
              onClick={() => {
                tour.reset();
                tour.start();
                navigate({ to: "/app" });
              }}
              variant="none"
              className="py-1"
              data-tour="profile-menu"
            >
              <ProfileMenuItem.Icon icon={PlayCircle} />
              <ProfileMenuItem.Label description="Reveja o tutorial do app">
                Ver Tutorial
              </ProfileMenuItem.Label>
              <ProfileMenuItem.Chevron />
            </ProfileMenuItem>
            <ProfileMenuItem
              onClick={() =>
                window.open("https://wa.me/5588999999999", "_blank")
              }
              variant="none"
              className="py-1"
            >
              <ProfileMenuItem.Icon icon={HelpCircle} />
              <ProfileMenuItem.Label>Ajuda</ProfileMenuItem.Label>
              <ProfileMenuItem.Chevron />
            </ProfileMenuItem>
            <ProfileMenuItem className="py-1" variant="none" onClick={() => { }}>
              <ProfileMenuItem.Icon icon={Info} />
              <ProfileMenuItem.Label>Sobre</ProfileMenuItem.Label>
              <ProfileMenuItem.Action>
                <span className="text-sm text-slate-400">v{APP_VERSION}</span>
              </ProfileMenuItem.Action>
            </ProfileMenuItem>
            <ProfileMenuItem
              onClick={() => setLogoutConfirmOpen(true)}
              variant="danger"
              className="py-1"
            >
              <ProfileMenuItem.Icon icon={LogOut} />
              <ProfileMenuItem.Label>Sair do aplicativo</ProfileMenuItem.Label>
              <ProfileMenuItem.Chevron />
            </ProfileMenuItem>
          </div>
        </div>

        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-slate-400">JapExpress v{APP_VERSION}</p>
        </div>
      </main>

      <PersonalDataSheet
        open={personalDataOpen}
        onOpenChange={setPersonalDataOpen}
        user={user}
        isEditing={isEditing}
        isSaving={isSaving}
        formData={formData}
        errors={errors}
        onEdit={handleEdit}
        onCancel={handleCancel}
        onSave={handleSave}
        onUpdateField={updateFormField}
        formatDate={formatDate}
        formatCPF={formatCPF}
      />

      <LocationPermissionSheet
        open={locationPermissionOpen}
        onOpenChange={setLocationPermissionOpen}
      />

      {logoutConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-3xl p-6 animate-in slide-in-from-bottom">
            <h3 className="text-lg font-semibold text-slate-900 text-center mb-2">
              Sair da conta?
            </h3>
            <p className="text-sm text-slate-500 text-center mb-6">
              Você precisará fazer login novamente para acessar sua conta.
            </p>
            <div className="space-y-3">
              <Button
                variant="destructive"
                className="w-full h-12 rounded-xl"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  handleLogout();
                }}
              >
                <LogOut className="w-5 h-5 mr-2" />
                Sair da conta
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
